import { Fragment, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateHealthQueries, invalidateManpowerConfigQueries } from '@/lib/queryClient';
import TrainingProgressTab from './TrainingProgressTab';
import FeedbackThread from './FeedbackThread';
import { CATEGORY_LABEL, MINE_KEY, firstLine, fmtWhen, statusLabel } from '@/lib/feedbackMine';
import EquipmentRatesTab from './EquipmentRatesTab';
import {
  adminApi,
  impersonation,
  manpowerApi,
  feedbackApi,
  type AdminUser,
  type AdminDivision,
  type AdminRole,
  type AdminPermission,
  type AdminOwnedEquipment,
  type FeedbackItem,
} from '../../lib/apiClient';
import { fmtInstantDate } from '@/lib/dates';
import {
  DEFAULT_STANDARD_FOLDERS, STANDARD_FOLDERS_CONFIG_KEY,
  parseStandardFolders, serializeStandardFolders, standardFolderEntryError,
} from '@/lib/graphDocuments';
import { SageSyncPanel } from '../sage/SageSyncPanel';
import AddressReviewTab from './AddressReviewTab';
import RollUpsTab from './RollUpsTab';
import { PaycorSyncPanel } from '../paycor/PaycorSyncPanel';
import { TYPED_HEADCOUNTS, adminConfigLabel } from '../manpower/typedHeadcounts';
import { visibleAdminTabs, type AdminTab } from './adminTabs';
import {
  describeSync, feedbackSyncState, filterFeedback, outOfStepCount, syncNeedsAttention, type FeedbackFilter,
} from './feedbackSync';
import { LISTED_LIMIT, SHOW_BATCH_MAX, boardLabel, canHide, canShow, canStopTelling, isTicked, isUnreviewed, listMayBeCut, stoppedLabel, stoppedTitle, tickAll, toReviewCount, toggleTick, type Ticks } from './feedbackBoard';
import { HideDialog, ShowOnBoardDialog, StopTellingDialog } from './FeedbackBoardControls';
import { MergeDialog } from './MergeDialog';
import { canMerge, combinedLabel, isMerged } from './feedbackMerge';

// ─── Shared style constants ────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 'var(--ew-fs-caption)',
  letterSpacing: 'var(--ew-ls-caption)',
  textTransform: 'uppercase',
  color: 'var(--ew-n-500)',
  background: 'var(--ew-n-50)',
  borderBottom: '1px solid var(--ew-n-200)',
  whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--ew-n-100)',
  fontSize: 'var(--ew-fs-body-sm)',
  verticalAlign: 'middle',
};

const TABLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: '#fff',
  border: '1px solid var(--ew-n-200)',
  borderRadius: 'var(--ew-r-lg)',
  overflow: 'hidden',
};

// ─── Add User form ─────────────────────────────────────────────────────────────

const INPUT: React.CSSProperties = {
  padding: '7px 10px',
  border: '1px solid var(--ew-n-300)',
  borderRadius: 'var(--ew-r-md)',
  fontSize: 'var(--ew-fs-body-sm)',
  fontFamily: 'var(--ew-font)',
  width: '100%',
};

function AddUserForm({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen]   = useState(false);
  const [oid, setOid]     = useState('');
  const [name, setName]   = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createUser = useMutation({
    mutationFn: () => adminApi.createUser({
      entraObjectId: oid.trim(),
      displayName:   name.trim(),
      email:         email.trim() || undefined,
    }),
    onSuccess: () => {
      setOid(''); setName(''); setEmail(''); setOpen(false); setError(null);
      onAdded();
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!open) {
    return (
      <button
        className="btn btn-secondary"
        style={{ marginBottom: 16 }}
        onClick={() => setOpen(true)}
      >
        + Add User
      </button>
    );
  }

  return (
    <div style={{
      marginBottom: 20,
      padding: '16px 18px',
      border: '1px solid var(--ew-n-200)',
      borderRadius: 'var(--ew-r-lg)',
      background: 'var(--ew-n-50)',
    }}>
      <p style={{
        fontWeight: 600, marginBottom: 12,
        fontSize: 'var(--ew-fs-body-sm)', color: 'var(--ew-dark)',
      }}>
        Pre-register User
      </p>
      <p style={{ fontSize: 'var(--ew-fs-caption)', color: 'var(--ew-n-500)', marginBottom: 14 }}>
        Find the Object ID in <strong>Azure Portal → Entra ID → Users → [name] → Object ID</strong>.
        The user will be updated automatically when they first sign in.
      </p>

      {error && (
        <div style={{
          marginBottom: 12, padding: '8px 12px',
          background: '#FFF0F0', border: '1px solid #FFCDD2',
          borderRadius: 'var(--ew-r-md)', color: 'var(--ew-danger)',
          fontSize: 'var(--ew-fs-body-sm)',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--ew-fs-caption)', fontWeight: 600, marginBottom: 4, color: 'var(--ew-n-600)' }}>
            Entra Object ID *
          </label>
          <input
            style={INPUT}
            value={oid}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            onChange={(e) => setOid(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--ew-fs-caption)', fontWeight: 600, marginBottom: 4, color: 'var(--ew-n-600)' }}>
            Display Name *
          </label>
          <input
            style={INPUT}
            value={name}
            placeholder="Jane Smith"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--ew-fs-caption)', fontWeight: 600, marginBottom: 4, color: 'var(--ew-n-600)' }}>
            Email
          </label>
          <input
            style={INPUT}
            value={email}
            placeholder="jsmith@eastandwestbrook.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-primary"
          disabled={!oid.trim() || !name.trim() || createUser.isPending}
          onClick={() => createUser.mutate()}
        >
          {createUser.isPending ? 'Adding…' : 'Add User'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => { setOpen(false); setError(null); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Sub-tab: Users & Roles ────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient();
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: adminApi.listUsers,
  });
  const { data: allRoles = [] } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: adminApi.listRoles,
  });
  const { data: allDivisions = [] } = useQuery({
    queryKey: ['admin', 'divisions'],
    queryFn: adminApi.listDivisions,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [pending, setPending] = useState<string[]>([]);
  const [pendingDivs, setPendingDivs] = useState<number[]>([]);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Removed people are listed separately rather than filtered out entirely —
  // this is the only screen that can restore them, so hiding them here would
  // make a mis-click unrecoverable without a DB fix.
  const activeUsers  = users.filter((u) => !u.deletedAt);
  const removedUsers = users.filter((u) => u.deletedAt);

  const afterChange = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    // Pickers and the training roll-up both filter on removal, so they are
    // stale the moment this succeeds.
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['training-progress-all'] });
    /**
     * ⚠️ AND BOTH TODAY PAGES, because removing a PERSON now moves JOBS.
     *
     * Removing a user only sets `deleted_at`; their assignments stay put. The
     * leadership queries join `users ... AND deleted_at IS NULL`, so from that
     * moment every job they hold reads as "No project manager assigned" and
     * their name disappears from the health rows — and restoring them moves it
     * all back. NO PROJECT ROW IS TOUCHED, which is exactly why this writer was
     * on nobody's list of things that change a project view. (Codex, P2 on #513.)
     *
     * No project id: a removal can affect any number of jobs at once.
     */
    void invalidateHealthQueries(qc);
  };

  const removeUser = useMutation({
    mutationFn: (userId: string) => adminApi.removeUser(userId),
    onSuccess: () => { setRemoveError(null); afterChange(); },
    onError: (err: Error) => setRemoveError(err.message),
    onSettled: () => setBusyId(null),
  });

  const restoreUser = useMutation({
    mutationFn: (userId: string) => adminApi.restoreUser(userId),
    onSuccess: () => { setRemoveError(null); afterChange(); },
    onError: (err: Error) => setRemoveError(err.message),
    onSettled: () => setBusyId(null),
  });

  /**
   * Ask before removing, and say what is still pointing at them.
   *
   * The assignments are fetched first precisely so the question is answerable:
   * "remove this user?" is easy to say yes to, "remove this user, who is
   * still Primary PM on 6 live jobs?" is the one worth pausing on. Nothing is
   * reassigned either way — the jobs keep their name until someone changes it.
   */
  async function confirmRemove(user: AdminUser) {
    setBusyId(user.id);
    setRemoveError(null);
    const who = user.displayName ?? user.email ?? 'this user';
    let detail = '';
    try {
      const { projects } = await adminApi.userAssignments(user.id);
      if (projects.length) {
        const shown = projects.slice(0, 8)
          .map((p) => `  • ${p.jobNumber}${p.title ? ` — ${p.title}` : ''}  (${p.roles.join(', ')})`)
          .join('\n');
        const more = projects.length > 8 ? `\n  …and ${projects.length - 8} more` : '';
        detail =
          `\n\n${who} is still on ${projects.length} live job${projects.length === 1 ? '' : 's'}:\n`
          + `${shown}${more}\n\nThese are NOT reassigned — the jobs keep showing their name until you change it.`;
      }
    } catch {
      // The check is a courtesy, not a gate. If it fails, still allow the
      // removal but be honest that we could not look.
      detail = '\n\n(Could not check their current job assignments.)';
    }

    const ok = window.confirm(
      `Remove ${who}?\n\n`
      + 'They will not be able to sign in, and they disappear from person-pickers. '
      + 'Everything they have already done — daily logs, RFIs, notes — keeps their name on it. '
      + 'You can restore them from this page.'
      + detail,
    );
    if (ok) removeUser.mutate(user.id);
    else setBusyId(null);
  }

  const saveUser = useMutation({
    mutationFn: async ({ userId, roles, divisions }: { userId: string; roles: string[]; divisions: number[] }) => {
      await adminApi.setUserRoles(userId, roles);
      await adminApi.setUserDivisions(userId, divisions);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      // also refresh 'me' so the current user's own role/division changes take effect
      void qc.invalidateQueries({ queryKey: ['me'] });
      setEditingId(null);
    },
  });

  function startEdit(user: AdminUser) {
    setEditingId(user.id);
    setPending([...user.roles]);
    setPendingDivs([...(user.divisions ?? [])]);
  }

  function toggleRole(label: string, checked: boolean) {
    setPending((prev) =>
      checked ? [...prev, label] : prev.filter((r) => r !== label),
    );
  }

  function toggleDiv(id: number, checked: boolean) {
    setPendingDivs((prev) =>
      checked ? [...prev, id] : prev.filter((d) => d !== id),
    );
  }
  const divLabel = (id: number) => allDivisions.find((d) => d.id === id)?.label ?? `#${id}`;

  if (loadingUsers) {
    return <p style={{ padding: 24, color: 'var(--ew-n-500)' }}>Loading users…</p>;
  }

  return (
    <div>
      <AddUserForm onAdded={() => void qc.invalidateQueries({ queryKey: ['admin', 'users'] })} />
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        Click <strong>Edit</strong> on any user to change their roles and division access. Division grants let a
        user see all projects in those divisions (e.g. a Commercial Manager). Changes take effect on their next page load.
      </p>

      {removeError && (
        <div style={{
          marginBottom: 12, padding: '10px 14px',
          background: '#FFF0F0', border: '1px solid #FFCDD2',
          borderRadius: 'var(--ew-r-md)', color: 'var(--ew-danger)',
          fontSize: 'var(--ew-fs-body-sm)',
        }}>
          {removeError}
        </div>
      )}

      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>Name</th>
            <th style={TH}>Email</th>
            <th style={TH}>Roles</th>
            <th style={TH}>Division access</th>
            <th style={{ ...TH, width: 230 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {activeUsers.map((user) => {
            const isEditing = editingId === user.id;
            return (
              <tr key={user.id} style={{ background: isEditing ? 'var(--ew-n-50)' : '#fff' }}>
                <td style={TD}>{user.displayName ?? <em style={{ color: 'var(--ew-n-400)' }}>Unknown</em>}</td>
                <td style={{ ...TD, color: 'var(--ew-n-500)' }}>{user.email ?? '—'}</td>
                <td style={TD}>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {allRoles.map((role) => (
                        <label
                          key={role.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 'var(--ew-fs-body-sm)', cursor: 'pointer',
                            padding: '3px 8px',
                            border: '1px solid var(--ew-n-200)',
                            borderRadius: 'var(--ew-r-sm)',
                            background: pending.includes(role.label) ? 'var(--ew-green)' : '#fff',
                            color: pending.includes(role.label) ? '#fff' : 'var(--ew-dark)',
                            transition: 'background 0.15s, color 0.15s',
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{ display: 'none' }}
                            checked={pending.includes(role.label)}
                            onChange={(e) => toggleRole(role.label, e.target.checked)}
                          />
                          {role.label}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {user.roles.length === 0 ? (
                        <em style={{ color: 'var(--ew-n-400)', fontSize: 'var(--ew-fs-body-sm)' }}>No roles</em>
                      ) : (
                        user.roles.map((r) => (
                          <span
                            key={r}
                            style={{
                              padding: '2px 8px',
                              borderRadius: 'var(--ew-r-sm)',
                              background: r === 'Admin' ? 'var(--ew-dark)' : 'var(--ew-n-100)',
                              color: r === 'Admin' ? '#fff' : 'var(--ew-dark)',
                              fontSize: 'var(--ew-fs-caption)',
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                            }}
                          >
                            {r}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </td>
                <td style={TD}>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {allDivisions.map((d) => (
                        <label
                          key={d.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 'var(--ew-fs-body-sm)', cursor: 'pointer',
                            padding: '3px 8px',
                            border: '1px solid var(--ew-n-200)',
                            borderRadius: 'var(--ew-r-sm)',
                            background: pendingDivs.includes(d.id) ? 'var(--ew-green)' : '#fff',
                            color: pendingDivs.includes(d.id) ? '#fff' : 'var(--ew-dark)',
                            transition: 'background 0.15s, color 0.15s',
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{ display: 'none' }}
                            checked={pendingDivs.includes(d.id)}
                            onChange={(e) => toggleDiv(d.id, e.target.checked)}
                          />
                          {d.label}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(user.divisions ?? []).length === 0 ? (
                        <em style={{ color: 'var(--ew-n-400)', fontSize: 'var(--ew-fs-body-sm)' }}>None</em>
                      ) : (
                        (user.divisions ?? []).map((id) => (
                          <span
                            key={id}
                            style={{
                              padding: '2px 8px',
                              borderRadius: 'var(--ew-r-sm)',
                              background: 'var(--ew-n-100)',
                              color: 'var(--ew-dark)',
                              fontSize: 'var(--ew-fs-caption)',
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                            }}
                          >
                            {divLabel(id)}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </td>
                <td style={TD}>
                  {isEditing ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        disabled={saveUser.isPending}
                        onClick={() => saveUser.mutate({ userId: user.id, roles: pending, divisions: pendingDivs })}
                      >
                        {saveUser.isPending ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        onClick={() => startEdit(user)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        title="See the app exactly as this user does (read-only)"
                        onClick={() => impersonation.start(user.id, user.displayName)}
                      >
                        View as
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        title="Block sign-in and hide from pickers — history is kept, and this can be undone"
                        disabled={busyId === user.id}
                        onClick={() => void confirmRemove(user)}
                      >
                        {busyId === user.id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {removedUsers.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{
            margin: '0 0 6px', font: 'var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font)',
            letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)',
          }}>
            Removed ({removedUsers.length})
          </h3>
          <p style={{ marginBottom: 12, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
            These people cannot sign in and do not appear in person-pickers. Their name still shows on
            records they created. Restoring brings back their roles and division access exactly as they were.
          </p>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH}>Name</th>
                <th style={TH}>Email</th>
                <th style={TH}>Roles</th>
                <th style={TH}>Removed</th>
                <th style={{ ...TH, width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {removedUsers.map((user) => (
                <tr key={user.id} style={{ background: '#fff', color: 'var(--ew-n-500)' }}>
                  <td style={TD}>{user.displayName ?? <em style={{ color: 'var(--ew-n-400)' }}>Unknown</em>}</td>
                  <td style={{ ...TD, color: 'var(--ew-n-500)' }}>{user.email ?? '—'}</td>
                  <td style={{ ...TD, color: 'var(--ew-n-500)' }}>{user.roles.join(', ') || '—'}</td>
                  <td style={{ ...TD, color: 'var(--ew-n-500)', whiteSpace: 'nowrap' }}>{fmtInstantDate(user.deletedAt)}</td>
                  <td style={TD}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                      disabled={busyId === user.id}
                      onClick={() => { setBusyId(user.id); restoreUser.mutate(user.id); }}
                    >
                      {busyId === user.id ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Sub-tab: Divisions ────────────────────────────────────────────────────────

function DivisionsTab() {
  const qc = useQueryClient();
  const { data: divisions = [], isLoading } = useQuery({
    queryKey: ['admin', 'divisions'],
    queryFn: adminApi.listDivisions,
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue]   = useState('');
  const [newLabel, setNewLabel]     = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const createDiv = useMutation({
    mutationFn: (label: string) => adminApi.createDivision(label),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'divisions'] });
      setNewLabel('');
    },
  });

  const updateDiv = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) =>
      adminApi.updateDivision(id, label),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'divisions'] });
      setEditingId(null);
    },
  });

  const deleteDiv = useMutation({
    mutationFn: (id: number) => adminApi.deleteDivision(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'divisions'] });
      setDeleteError(null);
    },
    onError: (err: Error) => setDeleteError(err.message),
  });

  function startEdit(d: AdminDivision) {
    setEditingId(d.id);
    setEditValue(d.label);
  }

  if (isLoading) {
    return <p style={{ padding: 24, color: 'var(--ew-n-500)' }}>Loading divisions…</p>;
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        Divisions are used to group leaders on the Manpower schedule.
      </p>

      {deleteError && (
        <div style={{
          marginBottom: 12, padding: '10px 14px',
          background: '#FFF0F0', border: '1px solid #FFCDD2',
          borderRadius: 'var(--ew-r-md)', color: 'var(--ew-danger)',
          fontSize: 'var(--ew-fs-body-sm)',
        }}>
          Could not delete: {deleteError}
        </div>
      )}

      <table style={TABLE}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 48 }}>ID</th>
            <th style={TH}>Label</th>
            <th style={{ ...TH, width: 120 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {divisions.map((d) => (
            <tr key={d.id}>
              <td style={{ ...TD, color: 'var(--ew-n-400)' }}>{d.id}</td>
              <td style={TD}>
                {editingId === d.id ? (
                  <input
                    value={editValue}
                    autoFocus
                    style={{
                      padding: '4px 8px', border: '1px solid var(--ew-n-300)',
                      borderRadius: 'var(--ew-r-sm)', fontSize: 'var(--ew-fs-body-sm)',
                      width: '100%', fontFamily: 'var(--ew-font)',
                    }}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') updateDiv.mutate({ id: d.id, label: editValue });
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                ) : (
                  d.label
                )}
              </td>
              <td style={TD}>
                {editingId === d.id ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="btn btn-primary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      disabled={updateDiv.isPending}
                      onClick={() => updateDiv.mutate({ id: d.id, label: editValue })}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      onClick={() => setEditingId(null)}
                      aria-label={`Cancel editing ${d.label}`}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      onClick={() => startEdit(d)}
                      aria-label={`Edit ${d.label}`}
                    >
                      ✏
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      onClick={() => {
                        if (window.confirm(`Delete division "${d.label}"? This cannot be undone.`)) {
                          deleteDiv.mutate(d.id);
                        }
                      }}
                      aria-label={`Delete ${d.label}`}
                    >
                      🗑
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add new */}
      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={newLabel}
          placeholder="New division name…"
          style={{
            flex: 1, padding: '7px 10px',
            border: '1px solid var(--ew-n-300)', borderRadius: 'var(--ew-r-md)',
            fontSize: 'var(--ew-fs-body-sm)', fontFamily: 'var(--ew-font)',
          }}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newLabel.trim()) createDiv.mutate(newLabel.trim());
          }}
        />
        <button
          className="btn btn-primary"
          disabled={!newLabel.trim() || createDiv.isPending}
          onClick={() => createDiv.mutate(newLabel.trim())}
        >
          + Add
        </button>
      </div>
    </div>
  );
}

// ─── Sub-tab: Roles & Permissions ──────────────────────────────────────────────

const PERM_CHIP: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: 'var(--ew-fs-body-sm)', cursor: 'pointer',
  padding: '3px 8px',
  border: '1px solid var(--ew-n-200)',
  borderRadius: 'var(--ew-r-sm)',
  transition: 'background 0.15s, color 0.15s',
};

function PermissionChips({
  all, selected, onToggle,
}: {
  all: AdminPermission[];
  selected: string[];
  onToggle: (code: string, checked: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {all.map((p) => {
        const on = selected.includes(p.code);
        return (
          <label
            key={p.id}
            title={p.code}
            style={{
              ...PERM_CHIP,
              background: on ? 'var(--ew-green)' : '#fff',
              color: on ? '#fff' : 'var(--ew-dark)',
            }}
          >
            <input
              type="checkbox"
              style={{ display: 'none' }}
              checked={on}
              onChange={(e) => onToggle(p.code, e.target.checked)}
            />
            {p.label}
          </label>
        );
      })}
    </div>
  );
}

function RolesTab() {
  const qc = useQueryClient();
  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: adminApi.listRoles,
  });
  const { data: allPerms = [] } = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: adminApi.listPermissions,
  });
  const permLabel = (code: string) => allPerms.find((p) => p.code === code)?.label ?? code;

  const [editingId, setEditingId]   = useState<number | null>(null);
  const [editLabel, setEditLabel]   = useState('');
  const [editPerms, setEditPerms]   = useState<string[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel]     = useState('');
  const [newPerms, setNewPerms]     = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
    // current user's permissions may change if they edited a role they hold
    void qc.invalidateQueries({ queryKey: ['me'] });
  };

  const createRole = useMutation({
    mutationFn: () => adminApi.createRole({ label: newLabel.trim(), permissions: newPerms }),
    onSuccess: () => {
      invalidate();
      setNewLabel(''); setNewPerms([]); setShowCreate(false); setActionError(null);
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const updateRole = useMutation({
    mutationFn: () => adminApi.updateRole(editingId!, { label: editLabel.trim(), permissions: editPerms }),
    onSuccess: () => { invalidate(); setEditingId(null); setActionError(null); },
    onError: (err: Error) => setActionError(err.message),
  });

  const deleteRole = useMutation({
    mutationFn: (id: number) => adminApi.deleteRole(id),
    onSuccess: () => { invalidate(); setActionError(null); },
    onError: (err: Error) => setActionError(err.message),
  });

  function startEdit(role: AdminRole) {
    setActionError(null);
    setEditingId(role.id);
    setEditLabel(role.label);
    setEditPerms([...role.permissions]);
  }
  const toggle = (set: React.Dispatch<React.SetStateAction<string[]>>) =>
    (code: string, checked: boolean) =>
      set((prev) => (checked ? [...prev, code] : prev.filter((c) => c !== code)));

  if (isLoading) {
    return <p style={{ padding: 24, color: 'var(--ew-n-500)' }}>Loading roles…</p>;
  }

  return (
    <div>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        Roles bundle permissions. Toggle what each role can do, then assign roles to people on the{' '}
        <strong>Users &amp; Roles</strong> tab. Changes take effect on a user&apos;s next page load.
      </p>

      {actionError && (
        <div style={{
          marginBottom: 12, padding: '10px 14px',
          background: '#FFF0F0', border: '1px solid #FFCDD2',
          borderRadius: 'var(--ew-r-md)', color: 'var(--ew-danger)',
          fontSize: 'var(--ew-fs-body-sm)',
        }}>
          {actionError}
        </div>
      )}

      <table style={TABLE}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 200 }}>Role</th>
            <th style={TH}>Permissions</th>
            <th style={{ ...TH, width: 150 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => {
            const isAdmin   = role.label === 'Admin';   // locked: only role whose name the code relies on
            const isEditing = editingId === role.id;
            return (
              <tr key={role.id} style={{ background: isEditing ? 'var(--ew-n-50)' : '#fff', verticalAlign: 'top' }}>
                <td style={TD}>
                  {isEditing ? (
                    <input
                      value={editLabel}
                      autoFocus
                      style={{ ...INPUT, padding: '4px 8px' }}
                      onChange={(e) => setEditLabel(e.target.value)}
                    />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontWeight: 600, color: 'var(--ew-dark)' }}>{role.label}</span>
                      {role.isBuiltIn && (
                        <span style={{ fontSize: 'var(--ew-fs-caption)', color: 'var(--ew-n-400)' }}>Built-in</span>
                      )}
                    </div>
                  )}
                </td>
                <td style={TD}>
                  {isEditing ? (
                    <PermissionChips all={allPerms} selected={editPerms} onToggle={toggle(setEditPerms)} />
                  ) : isAdmin ? (
                    <em style={{ color: 'var(--ew-n-500)' }}>All permissions</em>
                  ) : role.permissions.length === 0 ? (
                    <em style={{ color: 'var(--ew-n-400)' }}>No permissions (read-only)</em>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {role.permissions.map((c) => (
                        <span key={c} style={{
                          padding: '2px 8px', borderRadius: 'var(--ew-r-sm)',
                          background: 'var(--ew-n-100)', color: 'var(--ew-dark)',
                          fontSize: 'var(--ew-fs-caption)', fontWeight: 600,
                        }}>
                          {permLabel(c)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={TD}>
                  {isAdmin ? (
                    <span style={{ color: 'var(--ew-n-400)', fontSize: 'var(--ew-fs-caption)' }}>🔒 Locked</span>
                  ) : isEditing ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        disabled={!editLabel.trim() || updateRole.isPending}
                        onClick={() => updateRole.mutate()}
                      >
                        {updateRole.isPending ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '5px 12px', fontSize: 'var(--ew-fs-body-sm)' }}
                        onClick={() => { setEditingId(null); setActionError(null); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                        onClick={() => startEdit(role)}
                        aria-label={`Edit ${role.label}`}
                      >
                        ✏ Edit
                      </button>
                      {!role.isBuiltIn && (
                        <button
                          className="btn btn-danger"
                          style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                          onClick={() => {
                            if (window.confirm(`Delete role "${role.label}"? This cannot be undone.`)) {
                              deleteRole.mutate(role.id);
                            }
                          }}
                          aria-label={`Delete ${role.label}`}
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Create new role */}
      {showCreate ? (
        <div style={{
          marginTop: 20, padding: '16px 18px',
          border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-lg)',
          background: 'var(--ew-n-50)',
        }}>
          <p style={{ fontWeight: 600, marginBottom: 12, fontSize: 'var(--ew-fs-body-sm)', color: 'var(--ew-dark)' }}>
            New Role
          </p>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 'var(--ew-fs-caption)', fontWeight: 600, marginBottom: 4, color: 'var(--ew-n-600)' }}>
              Role name *
            </label>
            <input
              style={{ ...INPUT, maxWidth: 280 }}
              value={newLabel}
              placeholder="e.g. Scheduler"
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 'var(--ew-fs-caption)', fontWeight: 600, marginBottom: 6, color: 'var(--ew-n-600)' }}>
              Permissions
            </label>
            <PermissionChips all={allPerms} selected={newPerms} onToggle={toggle(setNewPerms)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              disabled={!newLabel.trim() || createRole.isPending}
              onClick={() => createRole.mutate()}
            >
              {createRole.isPending ? 'Creating…' : 'Create Role'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { setShowCreate(false); setNewLabel(''); setNewPerms([]); setActionError(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="btn btn-secondary"
          style={{ marginTop: 16 }}
          onClick={() => { setShowCreate(true); setActionError(null); }}
        >
          + Add Role
        </button>
      )}
    </div>
  );
}

// ─── Sub-tab: Config ───────────────────────────────────────────────────────────

const CONFIG_LABELS: Record<string, string> = {
  totalAvailableMp: 'Total Available Manpower',
  showProjectCosts: 'Show Costs on Project Detail',
  // The Man Hours report's divisor: estimated MH = estimated labor $ ÷ this.
  // $58/MH loaded per the presidents (2026-08-18); editable here because "the
  // rate will change at some point" (product owner). ⚠️ Changing it recomputes every
  // job's derived hours, past jobs included — the report shows the rate it used.
  estLaborRatePerManHour: 'Estimated Labor $ per Man-Hour (Man Hours report)',
  [STANDARD_FOLDERS_CONFIG_KEY]: 'Standard Job Folders',
  // Temps active and Subcontractors (#584, #688). Kept and edited on the Crew
  // Schedule; labeled here so the raw key doesn't appear unexplained once it
  // has a row.
  ...Object.fromEntries(TYPED_HEADCOUNTS.map((f) => [f.key, adminConfigLabel(f)])),
};

// Keys rendered as an on/off toggle ('1' = on, '0' = off) instead of a text field.
const BOOL_CONFIG_KEYS = new Set(['showProjectCosts']);

// Keys holding a newline-separated list, edited in the panel below the table
// rather than a one-line input (a nine-line value in a table cell is unusable).
const LIST_CONFIG_KEYS = new Set([STANDARD_FOLDERS_CONFIG_KEY]);

// Keys that must hold a number greater than 0. Save is blocked client-side and
// the API 400s the labor rate anyway (manpower.service.setConfig) — a persisted
// junk rate would be silently ignored by readLaborRate's fallback, giving the
// admin a "successful" change that never takes effect (review, #352).
const POSITIVE_NUMBER_CONFIG_KEYS = new Set(['estLaborRatePerManHour', 'totalAvailableMp']);
const isPositiveNumber = (s: string) => {
  const t = s.trim();
  return t !== '' && Number.isFinite(Number(t)) && Number(t) > 0;
};

function ConfigTab() {
  const qc = useQueryClient();
  const configQuery = useQuery({
    queryKey: ['manpower', 'config'],
    queryFn: manpowerApi.config,
  });
  const config = configQuery.data;

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue]   = useState('');
  const [listOpen, setListOpen]     = useState(false);

  const saveConfig = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      manpowerApi.updateConfig(key, value),
    // Every mp_config cache, not only this tab's: the Crew Schedule, the Gap
    // Analysis and the typed boxes read the same rows under other keys.
    onSuccess: () => {
      void invalidateManpowerConfigQueries(qc);
      setEditingKey(null);
    },
  });

  if (configQuery.isPending) {
    return <p style={{ padding: 24, color: 'var(--ew-n-500)' }}>Loading config…</p>;
  }

  // ⚠️ Gated on HAVING data, not on "not loading" and not on isSuccess. A
  // query that exhausted its retries leaves data undefined with isLoading
  // false, and the synthesis below would then fabricate an absent
  // standardJobFolders row out of a failed fetch — showing the compiled
  // defaults as though they were stored, and letting an admin overwrite the
  // real taxonomy by saving what looks like a confirmation.
  //
  // isSuccess would be the wrong test in the other direction: a failed
  // BACKGROUND refetch keeps the cached data but drops isSuccess, and blanking
  // a working tab over real, recent data helps nobody (queryStaleness.spec.ts).
  //
  // The sibling tabs default to `= []` on failure instead. That is fine for a
  // read-only list; this tab is different because the fallback is ACTIONABLE.
  if (!config) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: 'var(--ew-danger, #b91c1c)', fontSize: 'var(--ew-fs-body-sm)', marginBottom: 12 }}>
          Couldn't load the configuration. Nothing is shown rather than risk
          editing against a stale or default list.
        </p>
        <button
          className="btn btn-secondary"
          style={{ padding: '6px 14px', fontSize: 'var(--ew-fs-body-sm)' }}
          disabled={configQuery.isFetching}
          onClick={() => { void configQuery.refetch(); }}
        >
          {configQuery.isFetching ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    );
  }

  // The table is driven by whatever rows mp_config holds, so a key that has
  // never been written would simply not appear — leaving an admin no way to
  // create it. Surface the list-valued keys regardless; a null value reads as
  // "no row yet", which is exactly what the editor's fallback expects. Safe
  // only because the guard above proved the fetch actually succeeded.
  const entries = Object.entries(config);
  for (const key of LIST_CONFIG_KEYS) {
    if (!entries.some(([k]) => k === key)) entries.push([key, null]);
  }
  const editBlocked = editingKey != null
    && POSITIVE_NUMBER_CONFIG_KEYS.has(editingKey)
    && !isPositiveNumber(editValue);

  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        Application-wide configuration values.
      </p>
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>Setting</th>
            <th style={TH}>Value</th>
            <th style={{ ...TH, width: 100 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key}>
              <td style={{ ...TD, fontWeight: 500 }}>
                {CONFIG_LABELS[key] ?? key}
              </td>
              {LIST_CONFIG_KEYS.has(key) ? (
                <>
                  <td style={TD}>
                    <span style={{ color: 'var(--ew-n-500)' }}>
                      {parseStandardFolders(value).length} folder{parseStandardFolders(value).length === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td style={TD}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      onClick={() => { setEditingKey(null); setListOpen((v) => !v); }}
                    >
                      {listOpen ? 'Close' : '✏ Edit list'}
                    </button>
                  </td>
                </>
              ) : BOOL_CONFIG_KEYS.has(key) ? (
                <>
                  <td style={TD}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={value === '1'}
                        disabled={saveConfig.isPending}
                        onChange={(e) => saveConfig.mutate({ key, value: e.target.checked ? '1' : '0' })}
                      />
                      <span style={{ fontSize: 'var(--ew-fs-body-sm)', color: 'var(--ew-n-500)' }}>
                        {value === '1' ? 'On' : 'Off'}
                      </span>
                    </label>
                  </td>
                  <td style={TD} />
                </>
              ) : (
              <>
              <td style={TD}>
                {editingKey === key ? (
                  <>
                    <input
                      value={editValue}
                      autoFocus
                      style={{
                        padding: '4px 8px',
                        border: `1px solid ${editBlocked ? 'var(--ew-danger, #b91c1c)' : 'var(--ew-n-300)'}`,
                        borderRadius: 'var(--ew-r-sm)', fontSize: 'var(--ew-fs-body-sm)',
                        width: '100%', fontFamily: 'var(--ew-font)',
                      }}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !editBlocked) saveConfig.mutate({ key, value: editValue });
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                    />
                    {editBlocked && (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ew-danger, #b91c1c)' }}>
                        Must be a number greater than 0.
                      </div>
                    )}
                    {saveConfig.isError && !editBlocked && (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ew-danger, #b91c1c)' }}>
                        The server rejected that value — it was not saved.
                      </div>
                    )}
                  </>
                ) : (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</span>
                )}
              </td>
              <td style={TD}>
                {editingKey === key ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="btn btn-primary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      disabled={saveConfig.isPending || editBlocked}
                      onClick={() => saveConfig.mutate({ key, value: editValue })}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      onClick={() => setEditingKey(null)}
                      aria-label={`Cancel editing ${CONFIG_LABELS[key] ?? key}`}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                    onClick={() => { saveConfig.reset(); setEditingKey(key); setEditValue(value ?? ''); }}
                  >
                    ✏ Edit
                  </button>
                )}
              </td>
              </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {listOpen ? (
        <StandardFoldersEditor
          value={config[STANDARD_FOLDERS_CONFIG_KEY] ?? null}
          onSave={(v) => saveConfig.mutateAsync({ key: STANDARD_FOLDERS_CONFIG_KEY, value: v })}
          onClose={() => setListOpen(false)}
        />
      ) : null}
    </div>
  );
}

// ─── Standard Job Folders editor ───────────────────────────────────────────────
// The taxonomy the Documents tab offers when a PM sets up (or creates) a
// SharePoint Job Folder. Stored as one name per line in mp_config, using the
// SAME parse / serialize / validate helpers the Documents tab uses — so what an
// admin can save here is exactly what SharePoint will accept there.
//
// ⚠️ The app-managed folders (RFIs / Submittals / Daily Log Photos / Drawings)
// are deliberately NOT editable here: they are compiled constants the uploaders
// write into, so renaming one in config would send uploads to a second,
// near-miss folder rather than renaming anything.

/** Exported for its spec — the state timing here is what review kept catching. */
export function StandardFoldersEditor({ value, onSave, onClose }: {
  value: string | null;
  onSave: (value: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const saved = parseStandardFolders(value);
  const [names, setNames] = useState<string[]>(saved);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy]   = useState(false);
  // Has the admin changed anything in THIS panel? Distinct from `dirty`, which
  // only compares against whatever the query currently holds.
  const [touched, setTouched] = useState(false);

  // ⚠️ `names` seeds from the prop once, but the config query can refetch while
  // the panel is open — on window focus, or from the invalidation fired by a
  // save the admin just made and then reopened on top of. A pristine panel must
  // show what is actually stored, not a snapshot from mount: otherwise `dirty`
  // compares stale names against the fresh value, lights up Save on a panel
  // nobody edited, and pressing it reverts whoever wrote the new value.
  // Once the admin HAS edited, their work wins — we never clobber it — and a
  // Save is then a deliberate overwrite rather than a silent revert.
  useEffect(() => {
    if (!touched) setNames(parseStandardFolders(value));
  }, [value, touched]);

  const dirty = serializeStandardFolders(names) !== serializeStandardFolders(saved);
  const has = (n: string) => names.some((x) => x.toLowerCase() === n.trim().toLowerCase());

  const edit = (next: string[]) => { setNames(next); setTouched(true); };

  const add = () => {
    // Rejects app-managed names too, not just names SharePoint refuses.
    const problem = standardFolderEntryError(draft);
    if (problem) { setError(problem); return; }
    if (has(draft)) { setError(`"${draft.trim()}" is already on the list.`); return; }
    edit([...names, draft.trim()]);
    setDraft(''); setError(null);
  };

  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= names.length) return;
    const next = [...names];
    [next[i], next[j]] = [next[j], next[i]];
    edit(next);
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await onSave(serializeStandardFolders(names));
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'The server rejected that list — it was not saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 16, padding: 16, background: '#fff',
      border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-lg)',
    }}>
      <div style={{ fontWeight: 600, fontSize: 'var(--ew-fs-body-sm)', marginBottom: 4 }}>
        Standard Job Folders
      </div>
      <p style={{ marginBottom: 12, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        Offered as a checklist when a PM sets up or creates a SharePoint Job Folder. Editing this
        list never touches folders that already exist — nothing is renamed, moved or deleted, and
        existing jobs pick the change up next time someone runs Set up standard folders.
      </p>

      {error ? (
        <div style={{ marginBottom: 10, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>
          {error}
        </div>
      ) : null}

      <ul style={{ listStyle: 'none', margin: '0 0 12px', padding: 0, maxWidth: 460 }}>
        {names.map((n, i) => (
          <li key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--ew-n-100)', fontSize: 'var(--ew-fs-body-sm)' }}>
            <span style={{ flex: 1 }}>📁 {n}</span>
            {/* ⚠️ busy disables EVERY editing control, not just Save. `save`
                captured `names` at click; an edit landing mid-request would be
                discarded without trace when the response closes the panel. */}
            <button className="btn btn-secondary" style={MINI} disabled={busy || i === 0}
              onClick={() => move(i, -1)} aria-label={`Move ${n} up`}>↑</button>
            <button className="btn btn-secondary" style={MINI} disabled={busy || i === names.length - 1}
              onClick={() => move(i, 1)} aria-label={`Move ${n} down`}>↓</button>
            <button className="btn btn-secondary" style={MINI} disabled={busy}
              onClick={() => { edit(names.filter((_, x) => x !== i)); setError(null); }}
              aria-label={`Remove ${n}`}>✕</button>
          </li>
        ))}
        {names.length === 0 ? (
          <li style={{ padding: '6px 0', color: 'var(--ew-n-400)', fontSize: 'var(--ew-fs-body-sm)' }}>
            No standard folders — the checklist will offer only the app-managed ones.
          </li>
        ) : null}
      </ul>

      <div style={{ display: 'flex', gap: 8, maxWidth: 460, marginBottom: 14 }}>
        <input
          style={INPUT}
          value={draft}
          disabled={busy}
          placeholder="Add a folder, e.g. Working Papers"
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 'var(--ew-fs-body-sm)', whiteSpace: 'nowrap' }}
          disabled={busy || !draft.trim()} onClick={add}>
          Add
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 'var(--ew-fs-body-sm)' }}
          disabled={busy || !dirty} onClick={save}>
          {busy ? 'Saving…' : 'Save list'}
        </button>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 'var(--ew-fs-body-sm)' }}
          disabled={busy} onClick={() => { setNames(saved); setTouched(false); setDraft(''); setError(null); onClose(); }}>
          Cancel
        </button>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 'var(--ew-fs-body-sm)', marginLeft: 'auto' }}
          disabled={busy} onClick={() => { edit([...DEFAULT_STANDARD_FOLDERS]); setError(null); }}>
          Reset to defaults
        </button>
      </div>
    </div>
  );
}

const MINI: React.CSSProperties = { padding: '2px 8px', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.4 };

// ─── Integrations ───────────────────────────────────────────────────────────────
// The one global home for the outbound integrations — Sage Intacct and the
// Paycor hours + roster feeds. The Sage "Sync now · all jobs"
// button lives here (not scattered per-tab/per-job); Materials & Change Mgmt
// show a read-only freshness line only. A nightly @Cron on the API runs the
// same pull automatically. Read-only FROM Intacct — nothing here writes back.
function IntegrationsTab() {
  return (
    <div style={{ maxWidth: 720 }}>
      <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-lg)/1 var(--ew-font)', color: 'var(--ew-dark)', marginBottom: 6 }}>
        Sage Intacct
      </h3>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 }}>
        Pulls purchase orders, committed cost, contract values and change orders from Sage Intacct for every job.
        This is the only place to trigger a company-wide sync — the Materials and Change Management tabs show the
        result read-only. The sync is one-way: the Project Tracker never writes anything back to Intacct.
      </p>
      <SageSyncPanel variant="full" />

      <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-lg)/1 var(--ew-font)', color: 'var(--ew-dark)', margin: '28px 0 6px' }}>
        Cost lines Intacct no longer has
      </h3>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 }}>
        The read-only check for cost lines the Project Tracker still counts but Intacct no longer has is now on
        Reports &rarr; Work-in-Progress, next to the month-end close. It needs Close access, so the controller can
        run it without being given Admin.
      </p>

      <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-lg)/1 var(--ew-font)', color: 'var(--ew-dark)', margin: '28px 0 6px' }}>
        Paycor hours and roster
      </h3>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)', lineHeight: 1.5 }}>
        Pulls daily field labor from Paycor — the actual crew and hours behind the Manpower tab — plus the employee
        roster. The Project Tracker owns both feeds; the safety tool reads them from here. When the nightly sync is on it pulls three
        reports: yesterday's hours for freshness, the previous work week again to pick up hours entered late, and the
        roster so new hires appear and leavers drop off. Read-only: the Project Tracker never writes back to Paycor.
      </p>
      <PaycorSyncPanel />
    </div>
  );
}

// ─── Sub-tab: Owned Equipment ────────────────────────────────────────────────────
// The catalog of E&W-owned units. Feeds the "Owned Equipment #" picker on the
// equipment edit form. Retire (don't delete) a unit when it leaves the fleet —
// the unit number stays on any job it was already assigned to.
function OwnedEquipmentTab() {
  const qc = useQueryClient();
  const { data: units = [], isLoading } = useQuery({
    queryKey: ['admin', 'owned-equipment'],
    queryFn: adminApi.listOwnedEquipment,
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [edit, setEdit] = useState({ unitNumber: '', description: '', equipmentClass: '' });
  const [nu, setNu] = useState({ unitNumber: '', description: '', equipmentClass: '' });
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'owned-equipment'] });
  const onError = (err: Error) => setError(err.message);

  const createM = useMutation({
    mutationFn: () => adminApi.createOwnedEquipment({
      unitNumber: nu.unitNumber.trim(),
      description: nu.description.trim() || undefined,
      equipmentClass: nu.equipmentClass.trim() || undefined,
    }),
    onSuccess: () => { invalidate(); setNu({ unitNumber: '', description: '', equipmentClass: '' }); setError(null); },
    onError,
  });
  const updateM = useMutation({
    mutationFn: (v: { id: number; data: { unitNumber?: string; description?: string | null; equipmentClass?: string | null; isActive?: boolean } }) =>
      adminApi.updateOwnedEquipment(v.id, v.data as any),
    onSuccess: () => { invalidate(); setEditingId(null); setError(null); },
    onError,
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => adminApi.deleteOwnedEquipment(id),
    onSuccess: () => { invalidate(); setError(null); },
    onError,
  });

  function startEdit(u: AdminOwnedEquipment) {
    setEditingId(u.id);
    setEdit({ unitNumber: u.unitNumber, description: u.description ?? '', equipmentClass: u.equipmentClass ?? '' });
  }

  if (isLoading) return <p style={{ padding: 24, color: 'var(--ew-n-500)' }}>Loading owned equipment…</p>;

  const cellInput = (val: string, on: (v: string) => void) => (
    <input value={val} onChange={(e) => on(e.target.value)} style={{ padding: '4px 8px', border: '1px solid var(--ew-n-300)', borderRadius: 'var(--ew-r-sm)', fontSize: 'var(--ew-fs-body-sm)', width: '100%', fontFamily: 'var(--ew-font)' }} />
  );
  const addStyle = { padding: '7px 10px', border: '1px solid var(--ew-n-300)', borderRadius: 'var(--ew-r-md)', fontSize: 'var(--ew-fs-body-sm)', fontFamily: 'var(--ew-font)' } as const;
  const actBtn = { padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' } as const;

  return (
    <div style={{ maxWidth: 780 }}>
      <p style={{ marginBottom: 16, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        The list of E&amp;W-owned units that feeds the <strong>Owned Equipment #</strong> picker when assigning owned equipment to a job.
        When a unit leaves the fleet, <strong>Retire</strong> it — it drops off the picker but stays on any job it was already assigned to.
        Delete only for mistakes.
      </p>

      {error && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#FFF0F0', border: '1px solid #FFCDD2', borderRadius: 'var(--ew-r-md)', color: 'var(--ew-danger)', fontSize: 'var(--ew-fs-body-sm)' }}>
          {error}
        </div>
      )}

      {/* Add new */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={nu.unitNumber} placeholder="Unit # (e.g. SS24)" style={{ width: 150, ...addStyle }} onChange={(e) => setNu((p) => ({ ...p, unitNumber: e.target.value }))} />
        <input value={nu.description} placeholder="Description" style={{ flex: 1, minWidth: 180, ...addStyle }} onChange={(e) => setNu((p) => ({ ...p, description: e.target.value }))} />
        <input value={nu.equipmentClass} placeholder="Class" style={{ width: 160, ...addStyle }} onChange={(e) => setNu((p) => ({ ...p, equipmentClass: e.target.value }))} />
        <button className="btn btn-primary" disabled={!nu.unitNumber.trim() || createM.isPending} onClick={() => createM.mutate()}>+ Add</button>
      </div>

      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>Unit #</th>
            <th style={TH}>Description</th>
            <th style={{ ...TH, width: 160 }}>Class</th>
            <th style={{ ...TH, width: 80 }}>Status</th>
            <th style={{ ...TH, width: 160 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const editing = editingId === u.id;
            return (
              <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.55 }}>
                <td style={{ ...TD, fontWeight: 600 }}>{editing ? cellInput(edit.unitNumber, (v) => setEdit((p) => ({ ...p, unitNumber: v }))) : u.unitNumber}</td>
                <td style={TD}>{editing ? cellInput(edit.description, (v) => setEdit((p) => ({ ...p, description: v }))) : (u.description ?? '—')}</td>
                <td style={TD}>{editing ? cellInput(edit.equipmentClass, (v) => setEdit((p) => ({ ...p, equipmentClass: v }))) : (u.equipmentClass ?? '—')}</td>
                <td style={TD}>{u.isActive ? <span style={{ color: 'var(--ew-green-700)', fontWeight: 600 }}>Active</span> : <span style={{ color: 'var(--ew-n-500)' }}>Retired</span>}</td>
                <td style={TD}>
                  {editing ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-primary" style={actBtn} disabled={updateM.isPending || !edit.unitNumber.trim()} onClick={() => updateM.mutate({ id: u.id, data: { unitNumber: edit.unitNumber.trim(), description: edit.description.trim() || null, equipmentClass: edit.equipmentClass.trim() || null } })}>Save</button>
                      <button className="btn btn-secondary" style={actBtn} onClick={() => setEditingId(null)} aria-label="Cancel edit">✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary" style={actBtn} onClick={() => startEdit(u)} aria-label={`Edit ${u.unitNumber}`}>✏</button>
                      <button className="btn btn-secondary" style={actBtn} disabled={updateM.isPending} onClick={() => updateM.mutate({ id: u.id, data: { isActive: !u.isActive } })}>{u.isActive ? 'Retire' : 'Restore'}</button>
                      <button className="btn btn-danger" style={actBtn} onClick={() => { if (window.confirm(`Delete unit "${u.unitNumber}"? Retire it instead if it just left the fleet.`)) deleteM.mutate(u.id); }} aria-label={`Delete ${u.unitNumber}`}>🗑</button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          {units.length === 0 && (
            <tr><td style={{ ...TD, textAlign: 'center', color: 'var(--ew-n-400)' }} colSpan={5}>No owned equipment yet — add units above.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sub-tab: Feedback ───────────────────────────────────────────────────────────
// Beta feedback submitted via the in-app button. Each item is also a GitHub issue.
/** The columns of the table below: the conversation panel and the empty message span all of them. */
const FB_COLUMNS = 9;
function FeedbackTab() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({ queryKey: ['admin', 'feedback'], queryFn: feedbackApi.list });
  const [filter, setFilter] = useState<FeedbackFilter>('open');
  // The item whose conversation is open under its row. One at a time: the panel is a form, and two open at once would be two
  // half-written replies.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // The board: the items ticked to be shown, the preview that is open (a snapshot of what was ticked, so what the admin reads is what
  // is sent even if the list refreshes under it), and the item being hidden.
  const [selected, setSelected] = useState<Ticks>(new Map());
  const [showing, setShowing] = useState<FeedbackItem[] | null>(null);
  const [hiding, setHiding] = useState<FeedbackItem | null>(null);
  const [stopping, setStopping] = useState<FeedbackItem | null>(null);
  // The item being merged into another request: a snapshot, like the others, so what the admin reads is what is sent.
  const [merging, setMerging] = useState<FeedbackItem | null>(null);
  // A tick belongs to the view it was made in: another filter lists other rows, and an item ticked and then scrolled out of sight
  // would still be sent.
  useEffect(() => { setSelected(new Map()); }, [filter]);
  const tick = (i: FeedbackItem) => setSelected((prev) => toggleTick(prev, i));
  const toggle = useMutation({
    // `reason` says how it was closed (done, or won't do), and goes to GitHub as the close reason. A reopen has none.
    mutationFn: (v: { id: number; status: 'open' | 'closed'; reason?: 'completed' | 'not_planned' }) =>
      feedbackApi.updateStatus(v.id, v.status, { reason: v.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      // An admin who also follows the item sees it close under My feedback, and on the button's number.
      void qc.invalidateQueries({ queryKey: MINE_KEY });
    },
  });
  // Settle an item whose two sides disagree by sending ITS status to GitHub (the other way to settle it is the
  // Reopen button, which sends "open" there).
  const push = useMutation({
    mutationFn: (id: number) => feedbackApi.pushStatus(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });
  const sync = useMutation({
    mutationFn: feedbackApi.sync,
    onSuccess: async (s) => {
      // Wait for the refetch BEFORE moving the admin: switching first would show the filter over the old list, which may
      // not contain the flagged items yet, under a message saying nothing is out of step.
      await qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      // The items that need a decision are closed ones, which the default "Open" view hides. Take the admin to them.
      if (syncNeedsAttention(s)) setFilter('decide');
    },
  });
  const syncNote = sync.data ? describeSync(sync.data) : sync.isError ? { tone: 'error' as const, text: (sync.error as Error).message } : null;

  const downloadAttachment = async (a: { id: number; fileName: string }) => {
    const blob = await feedbackApi.downloadAttachment(a.id);
    const url = URL.createObjectURL(blob);
    const el = document.createElement('a');
    el.href = url; el.download = a.fileName;
    document.body.appendChild(el); el.click(); el.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (isLoading) return <p style={{ padding: 24, color: 'var(--ew-n-500)' }}>Loading feedback…</p>;
  const rows = filterFeedback(items as FeedbackItem[], filter);
  const outOfStep = outOfStepCount(items as FeedbackItem[]);
  const toReview = toReviewCount(items as FeedbackItem[]);
  const showable = rows.filter(canShow);
  // "Select all" is the UNREVIEWED ones only (see `selectableIds`): an item hidden earlier is ticked on its own.
  const unreviewed = rows.filter(isUnreviewed);
  // Only what is listed, can still be shown AND still reads as it did when it was ticked: after a refresh an item may have changed
  // under its tick (shown, or marked "not for the board", here or by another admin), and a changed item is not the one that was ticked.
  const ticked = showable.filter((i) => isTicked(selected, i));
  // ⚠️ The list holds the newest LISTED_LIMIT items, so "nothing to review" cannot be said of what is not in it.
  const mayBeCut = listMayBeCut((items as FeedbackItem[]).length);

  return (
    <div>
      <p style={{ marginBottom: 12, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        Feedback submitted from the in-app <strong>💬 Feedback</strong> button. Each item is also filed as a GitHub issue for triage/build tracking, and the two are kept in step: closing or reopening an item here does the same to its issue, and closing or reopening the issue on GitHub does the same here. Use <strong>Reply</strong> to answer everyone following the item: the person who sent it, and anyone who said “me too”. They see it under <strong>My feedback</strong>, and everyone can read it if the item is on the board. <strong>Done</strong> and <strong>Won’t do</strong> close the item and tell them which.
      </p>
      <p style={{ marginBottom: 12, color: 'var(--ew-n-500)', fontSize: 'var(--ew-fs-body-sm)' }}>
        The <strong>Board</strong> column says whether other people can read an item. Feedback sent before the board opened to everyone, and anything sent from a browser tab that was already open then, starts <strong>Unreviewed</strong>: only the admins and the person who sent it can read it, until you show it. So does all new feedback: <strong>new feedback is not public by default</strong>, and goes on the board only when you show it. (For a short time after the board opened, feedback went on it as soon as it was sent, unless its sender ticked <strong>Keep this private</strong>; those items stay on it.) The exception is feedback whose sender ticked <strong>Keep this private</strong>: that reads <strong>Private</strong>, is the sender’s choice, and nobody can change it. <strong>Show on board</strong> lets everyone signed in read the message and every reply on it, with the repliers’ names, and never the sender’s. <strong>Hide</strong> takes it off again.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`btn btn-${filter === 'open' ? 'primary' : 'secondary'}`} style={{ padding: '5px 12px' }} onClick={() => setFilter('open')}>Open</button>
        <button className={`btn btn-${filter === 'all' ? 'primary' : 'secondary'}`} style={{ padding: '5px 12px' }} onClick={() => setFilter('all')}>All</button>
        <button
          className={`btn btn-${filter === 'review' ? 'primary' : 'secondary'}`}
          style={{ padding: '5px 12px' }}
          onClick={() => setFilter('review')}
          title={mayBeCut ? `Items nobody has reviewed for the board yet, open or closed. The list holds the newest ${LISTED_LIMIT}, so there may be more than this.` : 'Items nobody has reviewed for the board yet, open or closed'}
        >
          To review ({toReview}{mayBeCut ? '+' : ''})
        </button>
        {(outOfStep > 0 || filter === 'decide') && (
          <button
            className={`btn btn-${filter === 'decide' ? 'primary' : 'secondary'}`}
            style={{ padding: '5px 12px' }}
            onClick={() => setFilter('decide')}
            title="Items whose status disagrees with their GitHub issue, or whose change has not reached GitHub yet"
          >
            ⚠ Out of step ({outOfStep})
          </button>
        )}
        <span style={{ flex: 1 }} />
        {unreviewed.length > 0 && (
          <button
            className="btn btn-secondary"
            style={{ padding: '5px 12px' }}
            onClick={() => setSelected((prev) => new Map([...prev, ...tickAll(rows)]))}
            title="Tick every unreviewed item in this list. You read each one before anything is shown. An item you hid earlier is not ticked: tick it on its own."
          >
            {unreviewed.length > SHOW_BATCH_MAX ? `Select the first ${SHOW_BATCH_MAX} unreviewed` : `Select all unreviewed (${unreviewed.length})`}
          </button>
        )}
        {ticked.length > 0 && (
          <>
            <button className="btn btn-secondary" style={{ padding: '5px 12px' }} onClick={() => setSelected(new Map())}>Clear</button>
            <button className="btn btn-primary" style={{ padding: '5px 12px' }} onClick={() => setShowing(ticked)}>
              Show on board ({ticked.length})…
            </button>
          </>
        )}
        <button
          className="btn btn-secondary"
          style={{ padding: '5px 12px' }}
          disabled={sync.isPending}
          onClick={() => sync.mutate()}
          title="Compare every item with its GitHub issue now, instead of waiting for the nightly check"
        >
          {sync.isPending ? 'Syncing…' : 'Sync with GitHub'}
        </button>
      </div>
      {syncNote && (
        <p
          role="status"
          style={{
            margin: '0 0 12px', padding: '8px 12px', borderRadius: 'var(--ew-r-md)', fontSize: 'var(--ew-fs-body-sm)',
            border: `1px solid ${syncNote.tone === 'ok' ? 'var(--ew-n-200)' : syncNote.tone === 'error' ? 'var(--ew-danger)' : 'var(--ew-warning)'}`,
            color: syncNote.tone === 'error' ? 'var(--ew-danger)' : 'var(--ew-dark)',
          }}
        >
          {syncNote.text}
        </p>
      )}
      {filter === 'review' && mayBeCut && (
        <p role="note" style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 'var(--ew-r-md)', fontSize: 'var(--ew-fs-body-sm)', border: '1px solid var(--ew-warning)' }}>
          This list holds the newest {LISTED_LIMIT} items, plus any that are out of step with GitHub, so older items waiting for review may not be in it.
        </p>
      )}
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 120 }}>When</th>
            <th style={TH}>Who</th>
            <th style={{ ...TH, width: 70 }}>Type</th>
            <th style={TH}>Feedback</th>
            <th style={TH}>Page</th>
            <th style={TH}>Files</th>
            <th style={{ ...TH, width: 130 }}>Issue</th>
            <th style={{ ...TH, width: 100 }}>Status</th>
            <th style={{ ...TH, width: 120 }}>Board</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i: FeedbackItem) => (
            <Fragment key={i.id}>
            <tr style={{ opacity: i.status === 'closed' ? 0.55 : 1 }}>
              <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--ew-n-500)', fontSize: 12 }}>
                {fmtWhen(i.createdAt)}
              </td>
              <td style={TD}>{i.submitterName ?? '—'}<div style={{ fontSize: 11, color: 'var(--ew-n-500)' }}>{i.submitterRoles ?? ''}</div></td>
              <td style={TD}>{CATEGORY_LABEL[i.category] ?? i.category}</td>
              <td style={{ ...TD, maxWidth: 340, whiteSpace: 'pre-wrap' }}>
                {i.message}
                <div>
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: 6, padding: '2px 8px', fontSize: 11 }}
                    aria-expanded={expandedId === i.id}
                    onClick={() => setExpandedId(expandedId === i.id ? null : i.id)}
                  >
                    💬 {i.followerCount == null ? 'Replies unavailable' : (i.replies?.length ?? 0) === 0 ? 'Reply' : `${i.replies!.length} ${i.replies!.length === 1 ? 'reply' : 'replies'}`}
                  </button>
                </div>
              </td>
              <td style={{ ...TD, color: 'var(--ew-n-500)', fontSize: 12, wordBreak: 'break-all', maxWidth: 160 }}>{i.page ?? '—'}</td>
              <td style={TD}>
                {i.attachments && i.attachments.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {i.attachments.map((a) => (
                      <button key={a.id} onClick={() => downloadAttachment(a)}
                        style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--ew-green)', cursor: 'pointer', textAlign: 'left', fontSize: 12, textDecoration: 'underline', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={a.fileName}>
                        📎 {a.fileName}
                      </button>
                    ))}
                  </div>
                ) : <span style={{ color: 'var(--ew-n-400)' }}>—</span>}
              </td>
              <td style={TD}>
                {i.githubIssueUrl
                  ? <a href={i.githubIssueUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--ew-green)', fontWeight: 600 }}>#{i.githubIssueNumber}</a>
                  : <span style={{ color: 'var(--ew-n-400)' }} title="Not filed to GitHub (integration off or the call failed)">—</span>}
                {(() => {
                  const state = feedbackSyncState(i);
                  if (state === 'pending') {
                    return (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ew-warning)' }}
                        title="This change has not reached GitHub yet. It is retried every night, or press Sync with GitHub.">
                        ⚠ Not on GitHub yet
                      </div>
                    );
                  }
                  if (state === 'mismatch') {
                    return (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ew-warning)' }}>
                        <span title={`GitHub still shows this issue as ${i.githubState}.`}>⚠ Still {i.githubState} on GitHub</span>
                        <button
                          className="btn btn-secondary"
                          style={{ display: 'block', marginTop: 4, padding: '2px 8px', fontSize: 11 }}
                          disabled={push.isPending}
                          onClick={() => push.mutate(i.id)}
                          title={`Change the GitHub issue to match this item. To change this item to match GitHub instead, use ${i.status === 'closed' ? 'Reopen' : 'Close'}.`}
                        >
                          {i.status === 'closed' ? 'Close it on GitHub' : 'Reopen it on GitHub'}
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
              </td>
              <td style={TD}>
                {(i.status === 'closed' || isMerged(i)) && (
                  // An item an admin merged into another reads as that, not as "Closed": it is a duplicate with somewhere to go.
                  <div style={{ marginBottom: 4, fontSize: 11, color: 'var(--ew-n-500)' }}>{combinedLabel(i, items as FeedbackItem[]) ?? statusLabel(i.status, i.closedReason)}</div>
                )}
                {i.status === 'open' && expandedId === i.id ? (
                  // ⚠️ NOT THE QUICK BUTTONS WHILE THE PANEL IS OPEN. They close with no note, and the panel has the same two buttons
                  // WITH one: an admin who typed a note and pressed the row's Done instead would close the item and tell the people
                  // following it nothing, with the note still sitting unsent in the box.
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--ew-n-500)' }}>Close it from the panel below.</span>
                    {canMerge(i) && <MergeButton item={i} onMerge={setMerging} />}
                  </div>
                ) : i.status === 'open' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ id: i.id, status: 'closed', reason: 'completed' })}
                      title="Close it as done. The people following it are told. To say why or what changed, use Reply."
                    >
                      Done
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ id: i.id, status: 'closed', reason: 'not_planned' })}
                      title="Close it as something we will not do. The people following it are told."
                    >
                      Won’t do
                    </button>
                    {canMerge(i) && <MergeButton item={i} onMerge={setMerging} />}
                  </div>
                ) : isMerged(i) ? (
                  // ⚠️ NO REOPEN FOR A MERGED ITEM. Reopening would not undo the merge (the people are already on the other request and nothing takes them off),
                  // it would only leave a hidden item that is open and can never be merged again, so the button is not offered.
                  <span style={{ fontSize: 11, color: 'var(--ew-n-500)' }} title="Its followers are on the other request, and nothing here takes a merge back.">Merged: it cannot be reopened from here.</span>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ id: i.id, status: 'open' })}
                    >
                      Reopen
                    </button>
                    {canMerge(i) && <MergeButton item={i} onMerge={setMerging} />}
                  </div>
                )}
              </td>
              <td style={TD}>
                {/* ⚠️ UNKNOWN is not "Unreviewed": the API could not say (the board migration has not been run there), so nothing is offered. */}
                <div
                  style={{ fontSize: 12, fontWeight: 600, color: i.boardVisibility == null ? 'var(--ew-warning)' : 'var(--ew-dark)' }}
                  title={
                    isMerged(i) ? 'Hidden because it was combined into another request, and it is not shown again.'
                    : i.boardVisibility === 'private' ? 'The sender asked to keep this to themselves and the admins. Nobody can change that.'
                      : i.boardVisibility == null ? 'The board could not be read for this list, so it cannot be changed from here.'
                        : undefined
                  }
                >
                  {boardLabel(i.boardVisibility)}
                </div>
                {canShow(i) && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isTicked(selected, i)}
                      onChange={() => tick(i)}
                      aria-label={`Select item ${i.id} to show on the board`}
                    />
                    Select
                  </label>
                )}
                {canHide(i) && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: 4, padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setHiding(i)}
                    title={i.boardVisibility === 'unreviewed' ? 'Mark it as reviewed and not for the board' : 'Take it off the board'}
                  >
                    {i.boardVisibility === 'unreviewed' ? 'Not for the board…' : 'Hide…'}
                  </button>
                )}
                {/* A HIDDEN item whose followers still hear about it: the removal that Hide offers as a tick, for the one hidden without it. */}
                {canStopTelling(i) && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: 4, padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setStopping(i)}
                    aria-label={stoppedLabel(i)}
                    title={stoppedTitle(i)}
                  >
                    Stop telling followers…
                  </button>
                )}
              </td>
            </tr>
            {expandedId === i.id && (
              <tr>
                <td style={{ ...TD, background: 'var(--ew-n-50)' }} colSpan={FB_COLUMNS}><FeedbackThread item={i} /></td>
              </tr>
            )}
            </Fragment>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={{ ...TD, textAlign: 'center', color: 'var(--ew-n-400)' }} colSpan={FB_COLUMNS}>
                {filter === 'decide' ? 'Nothing is out of step with GitHub.' : filter === 'review' ? (mayBeCut ? `Nothing in the newest ${LISTED_LIMIT} items is waiting to be reviewed.` : 'Nothing is waiting to be reviewed for the board.') : `No ${filter === 'open' ? 'open ' : ''}feedback yet.`}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {showing && <ShowOnBoardDialog items={showing} onClose={() => setShowing(null)} onDone={() => setSelected(new Map())} />}
      {hiding && <HideDialog item={hiding} onClose={() => setHiding(null)} />}
      {stopping && <StopTellingDialog item={stopping} onClose={() => setStopping(null)} />}
      {merging && <MergeDialog item={merging} items={items as FeedbackItem[]} onClose={() => setMerging(null)} />}
    </div>
  );
}

/** "Merge…" under an item's status. Named for the item, so a column of them is not a column of identical buttons to somebody using a screen reader. */
function MergeButton({ item, onMerge }: { item: FeedbackItem; onMerge: (i: FeedbackItem) => void }) {
  return (
    <button
      className="btn btn-secondary"
      style={{ padding: '4px 10px', fontSize: 'var(--ew-fs-body-sm)' }}
      onClick={() => onMerge(item)}
      aria-label={`Merge…: “${firstLine(item.message, 50)}”`}
      title="Combine it with another request on the board that asks for the same thing"
    >
      Merge…
    </button>
  );
}

// ─── Page shell ────────────────────────────────────────────────────────────────

const css = `
  .admin-subtabs {
    display: flex;
    gap: 4px;
    padding: 0 0 20px;
    border-bottom: 1px solid var(--ew-n-200);
    margin-bottom: 24px;
  }
  .admin-subtab {
    padding: 7px 16px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    cursor: pointer;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    color: var(--ew-n-600);
    transition: background var(--ew-dur-fast) var(--ew-ease),
                border-color var(--ew-dur-fast) var(--ew-ease);
  }
  .admin-subtab:hover { border-color: var(--ew-dark); color: var(--ew-dark); }
  .admin-subtab.active {
    background: var(--ew-dark);
    border-color: var(--ew-dark);
    color: #fff;
  }
`;

/**
 * `isAdmin` is the Admin ROLE. Anyone else who reaches this page holds
 * `feedback.manage` (the Feedback Admin role) and is shown the Feedback tab
 * alone — see `visibleAdminTabs`. The rest of the panel is not merely hidden:
 * the API refuses it to them.
 */
export default function AdminPage({ isAdmin }: { isAdmin: boolean }) {
  const tabs = visibleAdminTabs(isAdmin);
  const [picked, setTab] = useState<AdminTab>(tabs[0].key);
  // Render from what this viewer may see, not from raw state, so a stale or
  // forged `picked` can never mount a panel the strip does not offer.
  const tab = tabs.some((t) => t.key === picked) ? picked : tabs[0].key;

  return (
    <>
      <style>{css}</style>
      <div style={{ maxWidth: 960 }}>
        <h2 style={{
          font: 'var(--ew-fw-semibold) var(--ew-fs-h4)/1 var(--ew-font)',
          color: 'var(--ew-dark)',
          marginBottom: 20,
        }}>
          Admin
        </h2>

        <div className="admin-subtabs">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              className={`admin-subtab${tab === key ? ' active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'users'           && <UsersTab />}
        {tab === 'roles'           && <RolesTab />}
        {tab === 'divisions'       && <DivisionsTab />}
        {tab === 'owned-equipment' && <OwnedEquipmentTab />}
        {tab === 'equipment-rates' && <EquipmentRatesTab />}
        {tab === 'feedback'        && <FeedbackTab />}
        {tab === 'training'        && <TrainingProgressTab />}
        {tab === 'config'          && <ConfigTab />}
        {tab === 'integrations'    && <IntegrationsTab />}
        {tab === 'job-addresses'   && <AddressReviewTab />}
        {tab === 'roll-ups'        && <RollUpsTab />}
      </div>
    </>
  );
}
